// Symmetric crypto for the local key vault.
//
// Design:
// - One AES-GCM 256 master key, derived from the user's passphrase via
//   PBKDF2-SHA256 with 600k iterations and a per-vault random salt (industry
//   recommendation as of 2024-2025; OWASP currently suggests ≥600k for SHA-256).
// - Each encrypted blob carries its own 12-byte nonce. We never reuse a nonce
//   under the same key — a fresh one is rolled on every re-encrypt.
// - Everything stays inside SubtleCrypto so the raw key bytes never leave
//   the browser's secure context.

const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

export interface SealedBlob {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

export function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

export function newSalt(): Uint8Array {
  return randomBytes(SALT_BYTES);
}

export async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function seal(key: CryptoKey, plaintext: string): Promise<SealedBlob> {
  const iv = randomBytes(IV_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  return { iv, ciphertext: new Uint8Array(ct) };
}

export async function open(key: CryptoKey, blob: SealedBlob): Promise<string> {
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: blob.iv as BufferSource },
    key,
    blob.ciphertext as BufferSource,
  );
  return new TextDecoder().decode(pt);
}

// Stable hex helpers for the random bytes we surface to the user (vaultId).
export function toHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) {
    s += b[i]!.toString(16).padStart(2, '0');
  }
  return s;
}
